import React, { useState, useEffect } from "react";
import { Card, Grid, Icon, TextField, Autocomplete, Box } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import {
  selectSx,
  autoCompleteProps,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { getDepartmentList } from "actions/department";
import {
  DATE_RANGE_OPTIONS,
  activityTypeOptions,
  DEFAULT_RECORDS_PER_PAGE,
} from "components/common/constant";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getSubActivityUniqueList } from "actions/subActivity";
import { getMicroActivityUniqueList } from "actions/microActivity";
import { getStationList } from "actions/station";
import {
  getIndustrialReportList,
  exportIndustrialReportData,
} from "actions/reports";
import { industrialReportList } from "constants/reports";
import { useNotify } from "components/CommonComponent/NotificationProvider";

function ActivityControlReport() {
  const dispatch = useDispatch();
  const [pageNum, setPageNum] = useState(1);
  const today = new Date().toISOString().split("T")[0];

  const [filters, setFilters] = useState({
    dateRange: "",

    startDate: "",
    endDate: "",

    filterParams: JSON.stringify([]),
  });
  const [showClearButton, setShowClearButton] = useState(false);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });

  const [exportLoading, setExportLoading] = useState(false);

  const { notify } = useNotify();

  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  const subActivityData = useSelector(
    (state) => state.subActivity.subActivityUniqueList,
  );

  const microActivityData = useSelector(
    (state) => state.microActivity.microActivityUniqueList,
  );

  const stationListData = useSelector((state) => state.station.stationList);

  const reportListData = useSelector(
    (state) => state.employeeActivityReport?.industrialReportList,
  );

  useEffect(() => {
    getIndustrialReportList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({
        type: industrialReportList,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (reportListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: reportListData?.data?.industrialReport?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (reportListData?.code || reportListData?.message) {
      setListState({
        isLoading: false,
        error:
          reportListData?.message || "Failed to load activity control report!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [reportListData]);

  useEffect(() => {
    getDepartmentList(dispatch);
    getSubActivityUniqueList(dispatch);
    getMicroActivityUniqueList(dispatch);
    getStationList(dispatch);
  }, [dispatch]);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = today.toISOString().split("T")[0];

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = endDate.toISOString().split("T")[0];

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.startDate) {
      filterParams.push({
        field: "LastUsed",
        condition: "gte",
        filterValues: [filters.startDate],
      });
    }

    if (filters.endDate) {
      filterParams.push({
        field: "LastUsed",
        condition: "lte",
        filterValues: [filters.endDate],
      });
    }

    if (filters.department) {
      filterParams.push({
        field: "DepartmentName",
        condition: "like",
        filterValues: [filters.department],
      });
    }

    if (filters.station) {
      filterParams.push({
        field: "StationName",
        condition: "like",
        filterValues: [filters.station],
      });
    }

    if (filters.activityCategory) {
      filterParams.push({
        field: "ActivityType",
        condition: "eq",
        filterValues: [filters.activityCategory],
      });
    }

    if (filters.subActivity) {
      filterParams.push({
        field: "SubActivityName",
        condition: "like",
        filterValues: [filters.subActivity],
      });
    }

    if (filters.microActivity) {
      filterParams.push({
        field: "MicroActivityName",
        condition: "like",
        filterValues: [filters.microActivity],
      });
    }

    return JSON.stringify(filterParams);
  };

  const subActivityOptions =
    subActivityData?.data?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const microActivityOptions =
    microActivityData?.data?.microActivities?.map((item) => ({
      id: item.ID,
      name: item.name,
    })) || [];

  const data =
    reportListData?.data?.industrialReport?.map((item, index) => ({
      srNo: index + 1,
      departmentName: item.departmentName || "-",
      stationName: item.stationName || "-",
      activityType: item.activityType || "-",
      subActivityName: item.subActivityName || "-",
      microActivityName: item.microActivityName || "-",
      lastUsed: item.lastUsed || "-",
      daysNotUsed: item.daysNotUsed || "-",
    })) || [];

  const handleSearch = () => {
    const filterParams = buildFilterParams();

    if (filterParams === filters.filterParams) {
      return;
    }

    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));

    setFilters((prev) => ({
      ...prev,
      filterParams,
    }));

    setShowClearButton(true);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleClear = () => {
    const today = new Date().toISOString().split("T")[0];

    setFilters({
      dateRange: "",
      projectNo: "",
      typeOfProduct: "",
      unitID: null,
      department: "",
      station: "",
      startDate: "",
      endDate: "",
      filterParams: JSON.stringify([]),
    });

    setShowClearButton(false);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr No",
      accessor: "srNo",
      width: "90px",
    },
    {
      Header: "Department",
      accessor: "departmentName",
      width: "165px",
      Cell: (cell) => wrapCell(cell.value || "-", "165px"),
    },
    {
      Header: "Station",
      accessor: "stationName",
      width: "200px",
      Cell: (cell) => wrapCell(cell.value || "-", "200px"),
    },
    {
      Header: "Activity Type",
      accessor: "activityType",
      width: "140px",
      Cell: (cell) => wrapCell(cell.value || "-", "140px"),
    },
    {
      Header: "Sub Activity",
      accessor: "subActivityName",
      width: "200px",
      Cell: (cell) => wrapCell(cell.value || "-", "200px"),
    },
    {
      Header: "Micro Activity",
      accessor: "microActivityName",
      width: "220px",
      Cell: (cell) => wrapCell(cell.value || "-", "220px"),
    },
    {
      Header: "Last Used",
      accessor: "lastUsed",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value || "-", "120px"),
    },
    {
      Header: "Days Not Used",
      accessor: "daysNotUsed",
      width: "150px",
      Cell: (cell) => wrapCell(cell.value || "-", "150px"),
    },
  ];

  const downloadIndustrialReportCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportIndustrialReportData(filters.filterParams);
      
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "IndustrialReport.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message || "IndustrialReport file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download industrialReport file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              {/* Header */}
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Activity Control Report
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>file_upload</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={downloadIndustrialReportCSV}
                  >
                    {exportLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Exporting...
                      </>
                    ) : (
                      "Export Report"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2}>
                <Grid container spacing={2}>
                  {/* Department */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentListData?.data?.Department || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        departmentListData?.data?.Department?.find(
                          (item) => item.name === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.name || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>

                  {/* Station Name */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={stationListData?.data?.station || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        stationListData?.data?.station?.find(
                          (stat) => stat.name === filters.station,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          station: newValue?.name || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select station" />
                      )}
                    />
                  </Grid>

                  {/* Activity Type */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={activityTypeOptions || []}
                      getOptionLabel={(option) => option.label || ""}
                      value={
                        activityTypeOptions?.find(
                          (type) => type.value === filters.activityCategory,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          activityCategory: newValue?.value || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Activity type"
                        />
                      )}
                    />
                  </Grid>

                  {/* Sub Activity */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={subActivityOptions}
                      value={
                        subActivityOptions.find(
                          (item) => item.name === filters.subActivity,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          subActivity: value?.name || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Sub Activity"
                        />
                      )}
                    />
                  </Grid>

                  {/* Micro Activity */}
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      disablePortal
                      size="small"
                      options={microActivityOptions}
                      value={
                        microActivityOptions.find(
                          (item) => item.name === filters.microActivity,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          microActivity: value?.name || "",
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Micro Activity"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters({
                            ...filters,
                            dateRange: "CUSTOM",
                          });
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters({
                            ...filters,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>

                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="End Date"
                            minDate={dayjs(filters.startDate)}
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6} md={4} lg="auto">
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {showClearButton && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Total Records */}
              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue">
                  Total Records: {reportListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              {/* Table */}
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No records found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={data || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (reportListData?.data?.totalRecords || 0) /
                        (reportListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ActivityControlReport;
